import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { channelQuotaKey } from '@/features/channel-quota/api/billing';
import { formatDate, formatTime } from '@/shared/lib/format';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { SegmentedControl } from '@/shared/ui/segmented-control';
import { Skeleton } from '@/shared/ui/skeleton';
import { Switch } from '@/shared/ui/switch';
import {
  adjustQuota,
  fetchAdminQuota,
  fetchAdminSettings,
  fetchQuotaAdjustments,
  marketingAdminKeys,
  saveConsentByTerms,
  type AdminCapacity,
  type QuotaAdjustment,
} from '../api/admin';
import { marketingKeys } from '../api/client';
import { toMarketingError } from '../api/errors';
import type { ApiChannel } from '../api/types';
import { formatCount } from '../model/format';

const CHANNELS: Array<{ channel: ApiChannel; label: string; unit: string; icon: typeof Mail }> = [
  { channel: 'email', label: 'E-mail', unit: 'e-mails', icon: Mail },
  { channel: 'sms', label: 'SMS', unit: 'SMS', icon: MessageSquare },
];

type Direction = 'add' | 'remove';

const DIRECTIONS = [
  { value: 'add' as const, label: 'Incluir' },
  { value: 'remove' as const, label: 'Retirar' },
];

const PROVIDER_LABELS: Record<string, string> = { smsdev: 'SMSDev', twilio: 'Twilio', mailersend: 'MailerSend' };

function limitText(value: number | null, unit: string): string {
  return value === null ? 'Sem teto' : `${formatCount(value)} ${unit}`;
}

/**
 * E-mail e SMS marketing de um passbook, para o master: saldo do ciclo,
 * consentimento pelo regulamento da loja e ajuste manual de créditos (incluir
 * ou retirar), com o histórico dos ajustes.
 */
export function PassbookMarketingAdmin({ idPassbook }: { idPassbook: string }) {
  const quota = useQuery({ queryKey: marketingAdminKeys.quota(idPassbook), queryFn: () => fetchAdminQuota(idPassbook) });
  const settings = useQuery({ queryKey: marketingAdminKeys.settings(idPassbook), queryFn: () => fetchAdminSettings(idPassbook) });
  const adjustments = useQuery({
    queryKey: marketingAdminKeys.adjustments(idPassbook),
    queryFn: () => fetchQuotaAdjustments(idPassbook),
  });

  if (quota.isLoading || settings.isLoading) return <Skeleton className="h-96 rounded-2xl" />;
  if (quota.isError || settings.isError) {
    const error = toMarketingError(quota.error ?? settings.error, 'Não foi possível ler o marketing desta loja.');
    return (
      <p className="rounded-2xl border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive" role="alert">
        {error.message}
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid items-start gap-4 lg:grid-cols-2">
        {CHANNELS.map((item) => (
          <ChannelCard
            key={item.channel}
            idPassbook={idPassbook}
            channel={item.channel}
            label={item.label}
            unit={item.unit}
            icon={item.icon}
            capacity={quota.data?.[item.channel] ?? null}
            consentByTerms={settings.data?.[item.channel].consentByTerms === true}
          />
        ))}
      </div>
      <AdjustmentHistory rows={adjustments.data ?? []} loading={adjustments.isLoading} />
    </div>
  );
}

function ChannelCard({
  idPassbook,
  channel,
  label,
  unit,
  icon: Icon,
  capacity,
  consentByTerms,
}: {
  idPassbook: string;
  channel: ApiChannel;
  label: string;
  unit: string;
  icon: typeof Mail;
  capacity: AdminCapacity | null;
  consentByTerms: boolean;
}) {
  const queryClient = useQueryClient();
  const [direction, setDirection] = useState<Direction>('add');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');

  // O app não refaz consulta ao montar (refetchOnMount: false): a cota da barra
  // lateral é refeita mesmo fora da tela, e o público do wizard (contagem por
  // consentimento) sai do cache para a próxima abertura buscar de novo.
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: marketingAdminKeys.all(idPassbook) });
    void queryClient.invalidateQueries({ queryKey: channelQuotaKey(idPassbook), refetchType: 'all' });
    queryClient.removeQueries({ queryKey: marketingKeys.audiences(channel, idPassbook) });
  };

  const consent = useMutation({
    mutationFn: (value: boolean) => saveConsentByTerms(idPassbook, channel, value),
    onSuccess: (_data, value) => {
      toast.success(value ? `Regulamento vale como aceite de ${label}.` : `${label} volta a exigir o aceite de cada pessoa.`);
      refresh();
    },
    onError: (error) => toast.error(toMarketingError(error, 'Não deu para salvar.').message),
  });

  const slots = Math.trunc(Number(amount));
  const validAmount = Number.isFinite(slots) && slots > 0;
  const adjust = useMutation({
    mutationFn: () => adjustQuota(idPassbook, { channel, slots: direction === 'add' ? slots : -slots, reason: reason.trim() }),
    onSuccess: () => {
      toast.success(direction === 'add' ? `${formatCount(slots)} ${unit} incluídos.` : `${formatCount(slots)} ${unit} retirados.`);
      setAmount('');
      setReason('');
      refresh();
    },
    onError: (error) => toast.error(toMarketingError(error, 'Não deu para ajustar os créditos.').message),
  });

  const providerBalance = channel === 'sms' ? capacity?.providerBalance ?? null : null;

  return (
    <section className="overflow-hidden rounded-2xl border border-border/60 bg-card">
      <h3 className="flex items-center gap-2 border-b border-border/60 px-4 py-3 text-sm font-semibold">
        <Icon className="size-4 text-muted-foreground" /> {label}
        {capacity ? (
          <span className="ml-auto text-xs font-normal text-muted-foreground">
            Ciclo renova em {formatDate(capacity.renewsOn)}
          </span>
        ) : null}
      </h3>

      <div className="space-y-5 p-4">
        {capacity ? (
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm sm:grid-cols-3">
            <div>
              <dt className="text-xs text-muted-foreground">Limite da loja no ciclo</dt>
              <dd className="font-medium tabular-nums">{limitText(capacity.companyLimit, unit)}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Usado</dt>
              <dd className="font-medium tabular-nums">{formatCount(capacity.companySent)} {unit}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Livre para aprovar</dt>
              <dd className="font-medium tabular-nums">{limitText(capacity.canApprove, unit)}</dd>
            </div>
            {providerBalance !== null ? (
              <div className="col-span-2 sm:col-span-3">
                <dt className="text-xs text-muted-foreground">
                  Saldo da conta {PROVIDER_LABELS[capacity.provider] ?? capacity.provider} da Fixe (todas as lojas)
                </dt>
                <dd className="font-medium tabular-nums">{formatCount(providerBalance)} SMS</dd>
              </div>
            ) : null}
          </dl>
        ) : (
          <p className="text-sm text-muted-foreground">Sem saldo para mostrar neste canal.</p>
        )}

        <div className="flex items-start justify-between gap-4 rounded-xl border border-border/60 p-3">
          <div className="min-w-0">
            <Label htmlFor={`consent-${channel}`} className="text-sm font-medium">
              Regulamento vale como aceite
            </Label>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Para quem se cadastrou sem responder à pergunta de {label}. Quem marcou "não" no cadastro, desligou {label} ou respondeu
              SAIR continua fora.
            </p>
          </div>
          <Switch
            id={`consent-${channel}`}
            checked={consentByTerms}
            disabled={consent.isPending}
            onCheckedChange={(value) => consent.mutate(value)}
          />
        </div>

        <form
          className="space-y-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (validAmount && reason.trim()) adjust.mutate();
          }}
        >
          <div className="flex flex-wrap items-end gap-3">
            <SegmentedControl value={direction} onChange={setDirection} options={DIRECTIONS} size="sm" />
            <div className="w-32">
              <Label htmlFor={`adjust-amount-${channel}`} className="text-xs text-muted-foreground">
                Quantidade
              </Label>
              <Input
                id={`adjust-amount-${channel}`}
                className="mt-1"
                inputMode="numeric"
                value={amount}
                onChange={(event) => setAmount(event.target.value.replace(/\D/g, ''))}
                placeholder="0"
                autoComplete="off"
              />
            </div>
          </div>
          <div>
            <Label htmlFor={`adjust-reason-${channel}`} className="text-xs text-muted-foreground">
              Motivo (fica no histórico)
            </Label>
            <Input
              id={`adjust-reason-${channel}`}
              className="mt-1"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Ex.: cortesia de lançamento"
              maxLength={300}
              autoComplete="off"
            />
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground">
              Vale para o ciclo atual. Retirar só alcança o que a loja ainda tem livre.
            </p>
            <Button type="submit" size="sm" disabled={!validAmount || !reason.trim() || adjust.isPending}>
              {adjust.isPending
                ? 'Aplicando…'
                : validAmount
                  ? `${direction === 'add' ? 'Incluir' : 'Retirar'} ${formatCount(slots)} ${unit}`
                  : direction === 'add'
                    ? 'Incluir'
                    : 'Retirar'}
            </Button>
          </div>
        </form>
      </div>
    </section>
  );
}

function AdjustmentHistory({ rows, loading }: { rows: QuotaAdjustment[]; loading: boolean }) {
  return (
    <section className="overflow-hidden rounded-2xl border border-border/60 bg-card">
      <h3 className="border-b border-border/60 px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        Ajustes manuais
      </h3>
      {loading ? (
        <Skeleton className="m-4 h-16 rounded-xl" />
      ) : rows.length === 0 ? (
        <p className="px-4 py-4 text-sm text-muted-foreground">Nenhum ajuste feito nesta loja.</p>
      ) : (
        <ul className="divide-y divide-border/60">
          {rows.map((row) => (
            <li key={row.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5 text-sm">
              <Badge variant={row.slots > 0 ? 'success' : 'warning'} className="tabular-nums">
                {row.slots > 0 ? '+' : '−'}
                {formatCount(Math.abs(row.slots))} {row.channel === 'email' ? 'e-mails' : 'SMS'}
              </Badge>
              <span className="min-w-0 flex-1 break-words">{row.message}</span>
              <span className="text-xs text-muted-foreground">
                {formatDate(row.createdAt)} {formatTime(row.createdAt)}
                {row.createdBy ? ` · ${row.createdBy}` : ''}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
